/**
 * One design field → one control.
 *
 * The design says the type; this picks the widget. Everything it needs arrives
 * as props — there is no fetching, no debouncing and no snapshot access here,
 * so the mapping stays something you can read in one sitting.
 *
 * A RESPONSIVE FIELD IS THE SAME CONTROL, ONE ORIENTATION AT A TIME.
 * `{portrait, landscape}` is a wrapper around a value, not a type of its own,
 * so it is unwrapped here and the control is the one its inner value would have
 * had. WHICH half is shown follows the preview — the arrangement the Scene
 * Editor and the playEngine version settings both use, because the value you
 * are editing should be the one you are looking at. The other half is untouched
 * by the write and reachable by flipping the preview.
 */

import React, { useState } from "react";
import _ from "lodash";
import { Divider, IconButton, ListSubheader, Menu, MenuItem, Tooltip } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { designControlKind } from "../../modules/playEngineDesign";
import {
  AssetField,
  BooleanField,
  ColorField,
  DropdownField,
  FieldRow,
  JsonField,
  NumberField,
  OrientationTag,
  SliderField,
  StringField,
  Vec2Field,
} from "./designFields";
import { useEditableValue } from "../../customHooks/useEditableValue";

/** Which half of a `{portrait, landscape}` pair a write lands on. */
function orientationKey(isPortrait) {
  return isPortrait ? "portrait" : "landscape";
}

/** The control for one concrete value — no orientation wrapper left. */
function controlFor(field, value, disabled, onChange, onCommit) {
  switch (designControlKind(field)) {
  case "dropdown":
    return (
      <DropdownField
        value={value}
        disabled={disabled}
        onChange={onChange}
        onCommit={onCommit}
        options={field.options}
      />
    );
  case "boolean":
    return <BooleanField value={value} disabled={disabled} onChange={onChange} onCommit={onCommit} />;
  case "color":
    return <ColorField value={value} disabled={disabled} onChange={onChange} />;
  case "string":
    return <StringField value={value} disabled={disabled} onChange={onChange} onCommit={onCommit} />;
  case "vec2":
    return (
      <Vec2Field
        value={value}
        disabled={disabled}
        onChange={onChange}
        onCommit={onCommit}
        min={field.min}
        max={field.max}
        step={field.step}
      />
    );
  case "slider":
    return (
      <SliderField
        value={value}
        disabled={disabled}
        onChange={onChange}
        onCommit={onCommit}
        min={field.min}
        max={field.max}
        step={field.step}
      />
    );
  case "number":
  case "integer":
    return (
      <NumberField
        value={value}
        disabled={disabled}
        onChange={onChange}
        onCommit={onCommit}
        min={field.min}
        max={field.max}
        step={field.step}
        integer={designControlKind(field) === "integer"}
      />
    );
  default:
    return (
      <JsonField
        value={value}
        disabled={disabled}
        onChange={onChange}
        onCommit={onCommit}
        fieldKey={field.path}
        label={field.label}
      />
    );
  }
}

/** A small pill, for the badges that hang off a row. */
function Tag({ text, color, title }) {
  return (
    <span
      title={title}
      style={{
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        color,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: "0 4px",
        lineHeight: "14px",
        cursor: title ? "help" : "default",
      }}
    >
      {text}
    </span>
  );
}

/**
 * Where a row goes, and whether it stays — the two things about a field that
 * are the design's business rather than the snapshot's.
 *
 * A MENU, NOT A DRAG. The outliner moves groups by dragging, and matching that
 * here was the first idea; it does not survive contact with these rows. A field
 * row IS a live control — a scrub number and a slider both own the pointer from
 * mousedown — so a drag would need its own grip, and the drop target sits in a
 * different scroll pane above, whose size the user can change. A menu costs one
 * click, reaches a group that is scrolled out of view, and is reachable from the
 * keyboard.
 */
function RowMenu({
  fieldPath,
  fieldType,
  groupEntries,
  currentGroupId,
  disabled,
  canMoveUp,
  canMoveDown,
  onEdit,
  onEditOptions,
  onReorder,
  onMove,
  onRemove,
}) {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);

  const targets = groupEntries.filter((entry) => entry.group.id !== currentGroupId);

  return (
    <>
      <Tooltip title="Edit, move or remove this setting" arrow>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            style={{ padding: 2, color: "#7f838a" }}
            onClick={(event) => setAnchor(event.currentTarget)}
          >
            <MoreVertIcon style={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        // Anchored to the button rather than the pointer so a long list opens
        // in a predictable place on a row near the bottom of the pane.
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {onEdit ? (
          <MenuItem
            style={{ fontSize: 12 }}
            onClick={() => {
              close();
              onEdit(fieldPath);
            }}
          >
            Edit setting…
          </MenuItem>
        ) : null}
        {onEditOptions && fieldType !== "asset" ? (
          <MenuItem
            style={{ fontSize: 12 }}
            onClick={() => {
              close();
              onEditOptions(fieldPath);
            }}
          >
            Allowed values…
          </MenuItem>
        ) : null}
        {onEdit || onEditOptions ? <Divider /> : null}
        {onReorder ? (
          <ListSubheader style={{ fontSize: 10, lineHeight: "22px", textTransform: "uppercase" }}>
            Order in this group
          </ListSubheader>
        ) : null}
        {onReorder ? (
          <MenuItem
            style={{ fontSize: 12 }}
            disabled={!canMoveUp}
            onClick={() => {
              close();
              onReorder(fieldPath, -1);
            }}
          >
            Move up
          </MenuItem>
        ) : null}
        {onReorder ? (
          <MenuItem
            style={{ fontSize: 12 }}
            disabled={!canMoveDown}
            onClick={() => {
              close();
              onReorder(fieldPath, 1);
            }}
          >
            Move down
          </MenuItem>
        ) : null}
        {onReorder ? <Divider /> : null}
        <ListSubheader style={{ fontSize: 10, lineHeight: "22px", textTransform: "uppercase" }}>
          Move to
        </ListSubheader>
        {targets.length === 0 && (
          <MenuItem disabled style={{ fontSize: 12 }}>
            No other group
          </MenuItem>
        )}
        {targets.map((entry) => (
          <MenuItem
            key={entry.group.id}
            style={{ fontSize: 12, paddingLeft: 16 + entry.depth * 12 }}
            onClick={() => {
              close();
              onMove(fieldPath, entry.group.id);
            }}
          >
            {entry.group.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          style={{ fontSize: 12, color: "#e57373" }}
          onClick={() => {
            close();
            onRemove(fieldPath);
          }}
        >
          Remove from design
        </MenuItem>
      </Menu>
    </>
  );
}

function ResponsiveField({ field, value, isPortrait, disabled, action, onChange, onCommit }) {
  // The pair arrives as a fresh object on every store notification even when
  // nothing in it changed, so this must follow the VALUE, not its identity —
  // see useEditableValue.
  // A scalar under a responsive field is a value that was never split. Seeding
  // both halves with it is what the playEngine's own split button does, and it
  // keeps the first edit from writing a pair with one half missing.
  const incoming = _.isPlainObject(value) ? value : { portrait: value, landscape: value };
  const { value: pair, ref: pairRef, set: setPair, release: releasePair } =
    useEditableValue(incoming);

  const key = orientationKey(isPortrait);
  // A pair the snapshot has not split yet still shows something rather than a
  // blank: the other orientation is what the engine would fall back to.
  const shown = pair[key] !== undefined ? pair[key] : pair[orientationKey(!isPortrait)];

  function changeOrientation(nextValue) {
    const nextPair = { ...pairRef.current, [key]: nextValue };
    setPair(nextPair);
    onChange(field.path, nextPair);
  }

  return (
    <FieldRow label={field.label} tooltip={field.help} readOnly={disabled} action={action}>
      <OrientationTag isPortrait={isPortrait} />
      {controlFor(
        field,
        shown,
        disabled || field.readOnly,
        changeOrientation,
        () => {
          releasePair();
          onCommit(field.path);
        }
      )}
    </FieldRow>
  );
}

/**
 * @param {object} props
 * @param {object} props.field      one normalized design field
 * @param {*}      props.value      the value at `field.path`, or undefined
 * @param {boolean} props.present   whether the snapshot has that path at all
 * @param {boolean} props.disabled
 * @param {boolean} props.isPortrait  which half of a responsive pair is shown
 * @param {object|null} [props.assetEntry]      the asset map entry, for an asset row
 * @param {string|null} [props.assetThumbnail]  bytes an <img> can show, if any
 * @param {boolean} [props.assetBusy]           a replace is in flight
 * @param {string}  [props.assetAccept]         file-input accept string
 * @param {(file: File) => void} [props.onAssetUpload]
 * @param {() => void} [props.onAssetPickFromLibrary]
 * @param {(assetKey: string) => void} [props.onNavigateToAsset]
 * @param {(path: string, value: *) => void} props.onChange
 * @param {(path: string) => void} props.onCommit
 * @param {string}  props.language          the language being edited
 * @param {boolean} props.isOverrideMode    edits belong to that language
 * @param {boolean} props.isOverridden      a translation sits on this path
 * @param {(path: string) => void} [props.onRevert]
 * @param {Array}   [props.groupEntries]    every group, for the move menu
 * @param {string}  [props.currentGroupId]  the group this row is in
 * @param {Array} [props.schemaOptions]  allowed values the ENGINE declares for
 *   this path, used only when the design lists none of its own
 * @param {(path: string) => void} [props.onEditField]
 * @param {(path: string) => void} [props.onEditFieldOptions]
 * @param {(path: string, offset: number) => void} [props.onReorderField]
 * @param {boolean} [props.canMoveUp]
 * @param {boolean} [props.canMoveDown]
 * @param {(path: string, groupId: string) => void} [props.onMoveField]
 * @param {(path: string) => void} [props.onRemoveField]
 */
export function DesignFieldRenderer({
  field: declaredField,
  value,
  present,
  disabled,
  isPortrait = true,
  assetEntry = null,
  assetThumbnail = null,
  assetBusy = false,
  assetAccept = "*",
  onAssetUpload = null,
  onAssetPickFromLibrary = null,
  onNavigateToAsset = null,
  onChange,
  onCommit = () => {},
  language,
  isOverrideMode = false,
  isOverridden = false,
  onRevert = null,
  groupEntries = null,
  currentGroupId = null,
  schemaOptions = null,
  onEditField = null,
  onEditFieldOptions = null,
  onReorderField = null,
  canMoveUp = false,
  canMoveDown = false,
  onMoveField = null,
  onRemoveField = null,
}) {
  // What the author wrote outranks what was discovered: a design that lists its
  // own options keeps them, and a row the engine says nothing about is drawn
  // exactly as before.
  const field =
    schemaOptions && schemaOptions.length && !(declaredField.options && declaredField.options.length)
      ? { ...declaredField, options: schemaOptions }
      : declaredField;

  // Where the row lives is a property of the DESIGN, so it is offered whatever
  // the snapshot has — a row the snapshot cannot fill is exactly the one someone
  // is most likely to want out of the design.
  const menu =
    groupEntries && onMoveField && onRemoveField ? (
      <RowMenu
        fieldPath={field.path}
        fieldType={field.type}
        groupEntries={groupEntries}
        currentGroupId={currentGroupId}
        disabled={disabled}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onEdit={onEditField}
        onEditOptions={onEditFieldOptions}
        onReorder={onReorderField}
        onMove={onMoveField}
        onRemove={onRemoveField}
      />
    ) : null;

  // An asset row names a slot, not a value: none of the value machinery below
  // applies to it — no orientation pair, and no language override, because a
  // replace goes to the asset every language shares.
  if (field.type === "asset") {
    if (!present) {
      return (
        <FieldRow label={field.label} tooltip={field.help} readOnly action={menu}>
          <span style={{ fontSize: 11, color: "#6f7278", fontStyle: "italic" }}>
            no asset &quot;{field.assetKey}&quot; in this snapshot
          </span>
        </FieldRow>
      );
    }

    return (
      <FieldRow label={field.label} tooltip={field.help} readOnly={disabled} action={menu}>
        <AssetField
          assetKey={field.assetKey}
          entry={assetEntry}
          thumbnail={assetThumbnail}
          accept={assetAccept}
          disabled={disabled || field.readOnly}
          busy={assetBusy}
          onUpload={disabled || field.readOnly ? null : onAssetUpload}
          onPickFromLibrary={disabled || field.readOnly ? null : onAssetPickFromLibrary}
          onNavigate={onNavigateToAsset}
        />
        {isOverrideMode && (
          // A translator swapping a picture must not believe they made a
          // per-language change: this write lands on the shared asset.
          <span style={{ display: "block", fontSize: 10, color: "#7f838a", marginTop: 2 }}>
            shared across languages
          </span>
        )}
      </FieldRow>
    );
  }

  // A design is shared by every snapshot from one template, so it will always
  // be slightly ahead of some of them. Saying so beats a row that silently
  // writes a new key, and beats hiding the row so nobody can tell it exists.
  if (!present) {
    return (
      <FieldRow label={field.label} tooltip={field.help} readOnly action={menu}>
        <span style={{ fontSize: 11, color: "#6f7278", fontStyle: "italic" }}>
          not in this snapshot
        </span>
      </FieldRow>
    );
  }

  const rowAction = (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {isOverrideMode && (
        <Tag
          text={language}
          color="#7fa7c9"
          title={`Edits in "${language}" are stored as an override`}
        />
      )}
      {isOverridden && onRevert && (
        <span
          role="button"
          tabIndex={0}
          title={`Remove the ${language} translation and fall back to English`}
          onClick={() => onRevert(field.path)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onRevert(field.path);
          }}
          style={{ fontSize: 11, color: "#c9a25b", cursor: "pointer", lineHeight: "14px" }}
        >
          revert
        </span>
      )}
      {menu}
    </div>
  );

  if (!field.responsive) {
    return (
      <FieldRow label={field.label} tooltip={field.help} readOnly={disabled} action={rowAction}>
        {controlFor(
          field,
          value,
          disabled || field.readOnly,
          (next) => onChange(field.path, next),
          () => onCommit(field.path)
        )}
      </FieldRow>
    );
  }

  return (
    <ResponsiveField
      field={field}
      value={value}
      isPortrait={isPortrait}
      disabled={disabled}
      action={rowAction}
      onChange={onChange}
      onCommit={onCommit}
    />
  );
}

/**
 * One row re-renders when ITS value changes, not when any variable does.
 *
 * The panel is told about every write to the shared variable store, and a design
 * group can hold dozens of rows. With values now keeping their identity across
 * an unrelated change (see applyGlobalVariableOverrides) and the callbacks held
 * stable by the container, this comparison is the thing that turns that into
 * work actually skipped.
 */
export default React.memo(DesignFieldRenderer);
